/* eslint-disable no-console */
import { log } from "./log";
import { createClient } from "@supabase/supabase-js";

export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
export const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const client = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true, // Persiste sesión en localStorage
    detectSessionInUrl: true, // Detecta tokens de magic link en URL
    autoRefreshToken: true,
    storageKey: "SupabaseAuthSession",
    multiTab: false,
  },
});

// eslint-disable-next-line no-unused-vars
const fetchData = async (url, method, body) => {
  try {
    const response = await fetch(url, {
      method,
      headers: {
        apikey: supabaseAnonKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    // Verificar si la respuesta fue exitosa (código de estado 2xx)
    if (response.ok) {
      // Verificar si la respuesta tiene contenido
      const contentType = response.headers.get("content-type");
      const hasContent = contentType && contentType.includes("application/json");

      // Si la respuesta es JSON, devolverla, sino se devuelve vacía
      return hasContent ? await response.json() : {};
    } else {
      // Capturar el mensaje de error del cuerpo de la respuesta
      const errorBody = await response.json();
      // Lanzar una excepción con la información del error
      throw errorBody;
    }
  } catch (error) {
    log.error("supabase fetchData:", error);
    throw error;
  }
};

const fetchDataAuthenticated = async (url, method, body, extraHeaders = {}) => {
  try {
    const { data: session } = await client.auth.getSession();
    const headers = {
      apikey: supabaseAnonKey,
      "Content-Type": "application/json",
      Authorization: session?.session ? `Bearer ${session.session.access_token}` : undefined,
      ...extraHeaders,
    };
    const response = await fetch(url, {
      method,
      headers: Object.fromEntries(Object.entries(headers).filter(([_, v]) => v !== undefined)),
      body: JSON.stringify(body),
    });
    if (response.ok) {
      const contentType = response.headers.get("content-type");
      const hasContent = contentType && contentType.includes("application/json");
      return hasContent ? await response.json() : {};
    } else {
      const errorBody = await response.json();
      throw errorBody;
    }
  } catch (error) {
    log.error("supabase fetchDataAuthenticated:", error);
    throw error;
  }
};

export { fetchDataAuthenticated };

export const supabase = {
  insert: async (tableName, data) => {
    const url = `${supabaseUrl}/rest/v1/${tableName}`;
    return await fetchDataAuthenticated(url, "POST", data);
  },

  insertReturning: async (tableName, data) => {
    const url = `${supabaseUrl}/rest/v1/${tableName}`;
    return await fetchDataAuthenticated(url, "POST", data, { Prefer: "return=representation" });
  },

  update: async (tableName, data, id) => {
    const url = `${supabaseUrl}/rest/v1/${tableName}?id=eq.${id}`;
    return await fetchDataAuthenticated(url, "PATCH", data);
  },

  remove: async (tableName, id) => {
    const url = `${supabaseUrl}/rest/v1/${tableName}?id=eq.${id}`;
    return await fetchDataAuthenticated(url, "DELETE");
  },

  // DELETE con filtro custom (ej: "cliente_id=eq.123")
  removeWhere: async (tableName, filterString) => {
    const url = `${supabaseUrl}/rest/v1/${tableName}?${filterString}`;
    return await fetchDataAuthenticated(url, "DELETE");
  },

  select: async (tableName, { filters = {}, orderBy = [] } = {}) => {
    const queryParams = new URLSearchParams();

    // Agregar filtros
    for (const [key, value] of Object.entries(filters)) {
      if (typeof value === "object" && (value.gte || value.lte)) {
        // Formatear fechas en formato ISO 8601
        const formattedGte = value.gte instanceof Date ? value.gte.toISOString() : value.gte;
        const formattedLte = value.lte instanceof Date ? value.lte.toISOString() : value.lte;
        // Agregar filtro de rango (gte, lte)
        if (formattedGte) queryParams.append(`${key}`, `gte.${formattedGte}`);
        if (formattedLte) queryParams.append(`${key}`, `lte.${formattedLte}`);
      } else if (typeof value === "object") {
        // Objeto: like (otros operadores como {in} no están soportados todavía)
        if (value.like) {
          // Formatear el valor para escapar los caracteres especiales
          const formattedValue = value.like.replace(/[%_]/g, "\\$&");
          queryParams.append(`${key}`, `like.%${formattedValue}%`);
        }
      } else if (value != null && !(typeof value === "number" && Number.isNaN(value))) {
        // Primitivos (string | number | boolean) → operador eq.
        // Guard: omitir null/undefined/NaN para NO generar `eq.NaN` (ej: `ano` undefined
        // en callers que no lo pasan, como VentasMiListaClientesV2 → debe quedar sin filtrar).
        queryParams.append(key, `eq.${value}`);
      }
    }

    // Agregar ordenamientos
    if (orderBy.length > 0) {
      if (typeof orderBy === "string") {
        // Si solo se proporciona un ordenamiento como cadena
        queryParams.append("order", orderBy);
      } else {
        // Si se proporciona una lista de ordenamientos como objetos
        orderBy.forEach((order) => {
          queryParams.append("order", `${order.column}.${order.ascending ? "asc" : "desc"}`);
        });
      }
    }

    const url = `${supabaseUrl}/rest/v1/${tableName}?${queryParams.toString()}`;
    return await fetchDataAuthenticated(url, "GET");
  },

  getCurrentUser: async () => {
    try {
      const {
        data: { user },
      } = await client.auth.getUser();

      if (user) {
        return user;
      } else throw new Error("No user found");
    } catch (error) {
      log.error("getCurrentUser:", error);
      throw error;
    }
  },

  loginWithEmail: async (email, pais) => {
    try {
      const isLocalhost = window.location.href.includes("localhost");

      log.debug("pais recibido en loginWithEmail:", pais); // Log del valor recibido
      log.debug("isLocalhost:", isLocalhost);

      const redirect = isLocalhost
        ? "http://localhost:3000/admin"
        : {
            Chile: "https://wecast.cl/admin",
            Perú: "https://visiona.pe/admin",
            Uruguay: "https://www.screenmedia.com.uy/admin",
            Region: "https://adigitalholding.com/admin",
          }[pais] || "https://adigitalholding.com";

      log.debug("redirect calculado:", redirect);

      const { data, error } = await client.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirect,
        },
      });

      if (error) throw error;

      return data;
    } catch (error) {
      log.error("loginWithEmail:", error);
      throw error;
    }
  },

  getSession: async () => {
    try {
      const { data } = await client.auth.getSession();
      if (data?.session !== undefined) {
        return data.session;
      } else throw new Error("No user found");
    } catch (error) {
      log.error("getSession:", error);
      throw error;
    }
  },

  logout: async () => {
    try {
      const { error } = await client.auth.signOut();

      if (error) throw error;

      return { success: true };
    } catch (error) {
      log.error("logout:", error);
      throw error;
    }
  },

  updateUsuario: async ({ apellido, nombre }) => {
    try {
      const currentDate = new Date();
      const newUserData = {
        apellido,
        nombre,
        updated_at: currentDate,
      };
      const { data, error } = await client.auth.update({ data: newUserData });
      log.debug("data", data);

      if (error) throw error;

      if (data) {
        log.debug("updateUsuario", data);
        return data;
      } else {
        throw new Error("No data");
      }
    } catch (error) {
      log.error("updateUsuario", error);
      throw error;
    }
  },

  onAuthStateChange: (callback) => client.auth.onAuthStateChange((_event, session) => callback(session)),
};
